import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import chatBotImage from '../images/ChatBot.png';

export type Who = 'user' | 'assistant';

export interface ChatMessage {
  who: Who;
  text: string;
  imageUrl?: string;
  timestamp: number;
}

interface ChatMessagesState {
  // Messages organized by project ID
  messagesByProject: Record<string, ChatMessage[]>;

  // Get messages for a specific project
  getMessages: (projectId: string) => ChatMessage[];

  // Add a message to a specific project
  addMessage: (projectId: string, message: Omit<ChatMessage, 'timestamp'>) => void;

  // Update the last message for a project (for streaming)
  updateLastMessage: (projectId: string, text: string) => void;

  // Clear messages for a specific project
  clearMessages: (projectId: string) => void;

  // Initialize messages from API data
  initializeMessages: (projectId: string, apiMessages: any[], userPhotoUrl?: string) => void;

  // Check if messages are initialized for a project
  isInitialized: (projectId: string) => boolean;

  // Mark project as initialized
  markInitialized: (projectId: string) => void;

  // Track initialization status
  initializedProjects: Set<string>;
}

export const useChatMessagesStore = create<ChatMessagesState>()(
  persist(
    (set, get) => ({
      messagesByProject: {},
      initializedProjects: new Set(),

      getMessages: (projectId: string) => {
        const state = get();
        return state.messagesByProject[projectId] || [];
      },

      addMessage: (projectId: string, message: Omit<ChatMessage, 'timestamp'>) => {
        set(state => ({
          messagesByProject: {
            ...state.messagesByProject,
            [projectId]: [
              ...(state.messagesByProject[projectId] || []),
              {
                ...message,
                timestamp: Date.now(),
              },
            ],
          },
        }));
      },

      updateLastMessage: (projectId: string, text: string) => {
        set(state => {
          const projectMessages = state.messagesByProject[projectId] || [];
          if (projectMessages.length === 0) return state;

          const updatedMessages = [...projectMessages];
          const lastMessage = updatedMessages[updatedMessages.length - 1];

          // Update the last message text
          updatedMessages[updatedMessages.length - 1] = {
            ...lastMessage,
            text: lastMessage.text + text,
          };

          return {
            messagesByProject: {
              ...state.messagesByProject,
              [projectId]: updatedMessages,
            },
          };
        });
      },

      clearMessages: (projectId: string) => {
        set(state => {
          const newMessagesByProject = { ...state.messagesByProject };
          delete newMessagesByProject[projectId];

          const newInitializedProjects = new Set(state.initializedProjects);
          newInitializedProjects.delete(projectId);

          return {
            messagesByProject: newMessagesByProject,
            initializedProjects: newInitializedProjects,
          };
        });
      },

      initializeMessages: (projectId: string, apiMessages: any[], userPhotoUrl?: string) => {
        const mappedMessages: ChatMessage[] = apiMessages.map((message: any) => {
          const who = message.role === 'assistant' ? 'assistant' : 'user';
          let text = '';
          if (message.contentList && message.contentList.length > 0) {
            const content = message.contentList[0];
            if (content.type === 'text' && content.text?.value) {
              text = content.text.value;
            }
          }
          const imageUrl = who === 'assistant' ? chatBotImage : userPhotoUrl;
          return {
            who,
            text,
            imageUrl,
            timestamp: Date.now(),
          };
        });

        set(state => ({
          messagesByProject: {
            ...state.messagesByProject,
            [projectId]: mappedMessages,
          },
          initializedProjects: new Set([...state.initializedProjects, projectId]),
        }));
      },

      isInitialized: (projectId: string) => {
        const state = get();
        return state.initializedProjects.has(projectId);
      },

      markInitialized: (projectId: string) => {
        set(state => ({
          initializedProjects: new Set([...state.initializedProjects, projectId]),
        }));
      },
    }),
    {
      name: 'chat-messages-storage',
      partialize: state => ({
        messagesByProject: state.messagesByProject,
        initializedProjects: Array.from(state.initializedProjects),
      }),
      onRehydrateStorage: () => state => {
        if (state && Array.isArray(state.initializedProjects)) {
          state.initializedProjects = new Set(state.initializedProjects);
        }
      },
    }
  )
);
